import axios from "axios";
import {
  getCurrentDateTime,
  getImageDimensionsFromBase64,
  rgbToHex,
} from "../utils";
import ExcelJS from "exceljs";
import { VersionApp } from "../../constant/app";

/**
 * Export HTML tables to Excel using ExcelJS library.
 * @param {string[]} tableIDs Array of table IDs to export
 * @param {string} filename Filename for the exported Excel file
 * @param {string[]} info Additional info to include in the worksheet
 * @param {string[]} titles Titles for each table
 * @param {string[]} images Base64 encoded images to include in the worksheet
 * @param {string[]} textAfterTable Additional text to include after each table
 * @param {string} user User info to include in the worksheet
 */
export async function exportTableToExcel(
  info,
  tableIDs,
  filename = "",
  titles,
  images,
  user,
  textAfterTable
) {
  // const dataType = "application/vnd.ms-excel; charset=utf-8";
  let tablesHTML = "";
  // console.log(getCurrentDateTime());
  // Collect HTML from each table
  tableIDs.forEach(function (tableID) {
    const tableSelect = document.getElementById(tableID);
    const tableHTML = tableSelect.outerHTML;
    tablesHTML += tableHTML;
  });

  // Specify the file name
  filename = filename
    ? filename + getCurrentDateTime() + ".xlsx"
    : "excel_data.xlsx";

  // Create a new ExcelJS Workbook
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Add the image to the worksheet if base64Img is provided
  // if (base64Img) {
  //   const image = workbook.addImage({
  //     base64: base64Img,
  //     extension: "png",
  //   });

  //   worksheet.addImage(image, {
  //     tl: { col: 4, row: 0 },
  //     ext: { width: 200, height: 50 },
  //   });
  // }

  const imageBuffer = await axios.get("../../../../ERS-logo.png", {
    responseType: "arraybuffer",
  });
  
  const imageId2 = workbook.addImage({
    buffer: imageBuffer.data,
    extension: "png",
  });

  worksheet.addImage(imageId2, {
    tl: { col: 2, row: 0 },
    ext: { width: 55, height: 40 },
  });

  // Read the image file as a buffer

  worksheet.getCell("A1").value = "Rapports Irrigation et Fertilisation";

  worksheet.getCell("A1").font = {
    color: { argb: "4f46e5" }, // Text color white
    bold: true, // Optional: Make the text bold
  };

  worksheet.getCell("C3").value = "V" + (VersionApp ? VersionApp : "1.0.0");

  worksheet.mergeCells("A1:A2");
  worksheet.getCell("A1").alignment = { vertical: "middle" };

  worksheet.addRow([info[0]]).font = {
    color: { argb: "4f46e5" }, // Text color white
    bold: true, // Optional: Make the text bold
  };

  worksheet.addRow([""]);
  worksheet.addRow([info[1]]);
  worksheet.addRow([info[2], info[3]]);
  worksheet.addRow([info[4], info[5]]);
  worksheet.addRow([""]);

  // Add a gap below the image
  let startRow = worksheet.lastRow ? worksheet.lastRow.number + 2 : 2;

  // Parse HTML and add it to the worksheet
  const parser = new DOMParser();
  const doc = parser.parseFromString(tablesHTML, "text/html");
  const tableElements = doc.querySelectorAll("table");

  tableElements.forEach((table, idx) => {
    const mergeCells = [];
    const mergedRanges = new Set(); // To track merged cells and avoid duplicates

    const rows = Array.from(table.querySelectorAll("tr"));
    const cellMatrix = [];

    if (titles && titles[idx]) {
      let row = worksheet.getRow(startRow);

      // Adding an empty row
      row.commit(); // Committing the row to add it

      // Adding a row with title and formatting
      row = worksheet.getRow(startRow + 1);
      row.values = [titles[idx]];
      row.font = {
        bold: true,
        underline: true,
      };
      row.commit(); // Committing the row to add it

      // Incrementing startRow for the next set of rows
      startRow += 3;
    }

    rows.forEach((tr, rowIndex) => {
      const row = worksheet.getRow(startRow + rowIndex);
      if (!cellMatrix[startRow + rowIndex]) {
        cellMatrix[startRow + rowIndex] = [];
      }
      let colIndex = 1;

      Array.from(tr.children).forEach((cell) => {
        // Skip columns that are already occupied by merged cells
        while (cellMatrix[startRow + rowIndex][colIndex]) {
          colIndex++;
        }

        const cellText = cell.textContent.trim();
        const colspan = parseInt(cell.getAttribute("colspan")) || 1;
        const rowspan = parseInt(cell.getAttribute("rowspan")) || 1;

        const cellObj = row.getCell(colIndex);

        // Check for alignment classes or styles
        // console.log(cell.textContent , cell.classList)

        const percentagePattern = /^[-+]?(\d+|\d*\.\d+)\s*%$/;

        if (
          cell.classList.contains("text-right") ||
          cell.style.textAlign === "right"
        ) {
          cellObj.alignment = { horizontal: "right" };
        } else if (
          cell.classList.contains("text-center") ||
          cell.style.textAlign === "center"
        )
          cellObj.alignment = { horizontal: "center" };
        else {
          // Default alignment (left or center, as per Excel's default)
          cellObj.alignment = { horizontal: "left" };
        }

        if (
          !isNaN(cellText.replaceAll(" ", "")) &&
          cellText !== "" &&
          !cell.classList.contains("excel-string")
        ) {
          cellObj.value = parseFloat(cellText.replaceAll(" ", ""));
          if (cellText.includes(".")) cellObj.numFmt = "### ### ### ### ##0.00";
          else cellObj.numFmt = "### ### ### ### ##0";
        } else if (percentagePattern.test(cellText)) {
          const percentage = parseFloat(cellText.replace(/\s|%$/, "")) / 100;
          cellObj.value = percentage;
          cellObj.numFmt = "0.00%";
        } else {
          cellObj.value = cellText; // Default handling for non-numeric, non-percentage values
        }

        // Apply header styling if the table header has the class "table_heeader"
        if (
          cell.classList.contains("table_heeader") ||
          tr.parentNode.classList.contains("table_heeader")
        ) {
          cellObj.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF374151" }, // Background color #f3593c
          };
          cellObj.font = {
            color: { argb: "FFFFFFFF" }, // Text color white
            bold: true, // Optional: Make the text bold
          };
        }

        // Apply row styling if the row has the class "row-total"
        if (tr.classList.contains("row-total")) {
          cellObj.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFABCB1" }, // Background color #fabcb1
          };
        }

        // add background color to the row if class list contains header_row
        if (tr.classList.contains("header_row")) {
          cellObj.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "56778191" }, // Background color #f3593c
          };
          cellObj.font = {
            color: { argb: "FFFFFFFF" }, // Text color white
            bold: true, // Optional: Make the text bold
          };
        }
        
        // Apply inline background color if present
        // const bgColor = tr.style.backgroundColor;

        // console.log(window.getComputedStyle(cell).backgroundColor);
        const bgColor = cell.style.backgroundColor || tr.style.backgroundColor;
        // console.log(window.getComputedStyle(cell));

        if (bgColor && bgColor !== "rgb(255, 255, 255)") {
          const hexColor = rgbToHex(bgColor);
          //console.log(bgColor);
          if (hexColor) {
            cellObj.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: hexColor },
            };
          }
        }

        for (let r = 0; r < rowspan; r++) {
          if (!cellMatrix[startRow + rowIndex + r]) {
            cellMatrix[startRow + rowIndex + r] = [];
          }
          for (let c = 0; c < colspan; c++) {
            cellMatrix[startRow + rowIndex + r][colIndex + c] = true;
          }
        }

        if (colspan > 1 || rowspan > 1) {
          const startRowIndex = startRow + rowIndex;
          const endRowIndex = startRow + rowIndex + rowspan - 1;
          const startColIndex = colIndex;
          const endColIndex = colIndex + colspan - 1;

          const mergeRange = `${startRowIndex}:${startColIndex}:${endRowIndex}:${endColIndex}`;

          // Check if this range has been merged before to avoid redundant merges
          if (!mergedRanges.has(mergeRange)) {
            mergeCells.push({
              startRow: startRowIndex,
              startCol: startColIndex,
              endRow: endRowIndex,
              endCol: endColIndex,
            });
            mergedRanges.add(mergeRange); // Mark this range as merged
          }
        }

        colIndex += colspan;
      });
    });

    // Perform merges after processing all cells to avoid overlapping issues
    mergeCells.forEach((merge) => {
      worksheet.mergeCells(
        merge.startRow,
        merge.startCol,
        merge.endRow,
        merge.endCol
      );
    });

    startRow += rows.length + 1; // Move startRow for the next table

    if (images && images[idx]) {
      const dimensions = getImageDimensionsFromBase64(images[idx]);
      const image = workbook.addImage({
        base64: images[idx],
        extension: "png",
      });
      worksheet.addImage(image, {
        tl: { col: 0, row: startRow },
        ext: dimensions,
      });
      startRow += dimensions.height / 20 + 2;
    }

    if (textAfterTable && textAfterTable[idx]) {
      const extraText = textAfterTable[idx].split("\n");
      extraText.forEach((element) => {
        worksheet.getRow(startRow).values = [element];
        startRow += 1;
      });
    }
  });

  const row = worksheet.getRow(startRow + 2);
  row.values = [
    "",
    "Date de génération: " + getCurrentDateTime(),
    "",
    "",
    "Utilisateur: " + user,
  ];

  // Iterate through each column to adjust width based on content
  worksheet.columns.forEach((column) => {
    let maxLength = 0;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const columnWidth = cell.value ? cell.value.toString().length : 0;
      maxLength = Math.max(maxLength, columnWidth);
    });
    column.width = maxLength < 10 ? 10 : maxLength + 2; // Set minimum column width
  });

  // Write the workbook to a buffer
  const buffer = await workbook.xlsx.writeBuffer();

  // Convert the buffer to a Blob
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Create a link element to prompt the user to download the blob
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
